// Multievento, pestaña Líneas y menús del formato nuevo: lo que se cobra y se
// cocina de cada jornada y su gestión completa (sustituye en el formato nuevo
// al gestor heredado JornadasManager, que queda oculto, no borrado):
// - Barra superior: Añadir jornada, Generar jornadas (con 0 o 1 jornadas),
//   Igualar jornadas a la cabecera (con la insignia de líneas distintas) y la
//   fila de totales (jornadas, personas y el total del servidor).
// - Cada jornada, en orden de fechas: cabecera (JornadaCabecera: fecha, horas
//   con "+1", personas, lugar y acciones: Editar jornada, Editar menús,
//   Duplicar, Eliminar y las dos órdenes de cocina), observaciones en línea,
//   sus líneas con las unidades editables en línea (PUT de la jornada con su
//   `menus_personalizados` completo, 600 ms después de teclear), Quitar y el
//   chip de barra libre, y su subtotal.
// - El aviso de las líneas que están en la cabecera y en ninguna jornada (no
//   se cobran ni se cocinan), con "Llevar a todas las jornadas".
// Todo por axios (api/api.js). El precio y el IVA siguen a la cabecera.
//
// Props:
// - presupuesto: el presupuesto (multievento)
// - onActualizado(presupuesto | null): tras un cambio en el servidor; con el
//   presupuesto que devuelve (o null para recargar la ficha)
// - barraLibre (opcional): {estado, guardando, error, alternar(entrada, activar, clave)}
//   para el chip "Barra libre" del lunch de cada jornada (el mismo de la tabla
//   principal). La línea vinculada no edita sus personas (siguen al lunch) y su
//   "Quitar" apaga el chip.
// - soloLectura: evento pasado, facturado o cancelado (sin botones de edición
//   ni campos; solo las descargas de las órdenes de cocina)
// - onOrdenCocina(jornadaId) / onOrdenCocinaNueva(jornadaId): descargas de la
//   orden de cocina antigua y de la nueva (los handlers de la ficha)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { presupuestosAPI } from '../../api/api';
import { toast } from '../../hooks/use-toast';
import useEsMovil from '../../hooks/useEsMovil';
import JornadaCabecera, { ObservacionesJornada } from './JornadaCabecera';
import EditarJornadaModal from './EditarJornadaModal';
import EditarMenusJornadaModal from './EditarMenusJornadaModal';
import { DuplicarJornadaModal, GenerarJornadasModal, NuevaJornadaModal } from './NuevaJornadaModal';
import {
  TEXTO_PRECIO_EN_CABECERA, esLineaBebida, esLineaVentaAparte, jornadasOrdenadas, lineaCabecera, lineaResuelta,
} from '../../utils/multieventoCabecera';
import {
  idsJornadasVacias, lineasConUdsCambiadas, textoJornada, textoPersonasGestorJornadas,
} from '../../utils/multieventoPantallas';

const formatEuros = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

const mismaLinea = (a, b) => {
  if (!a || !b) return false;
  if (a.linea_id || b.linea_id) return a.linea_id === b.linea_id;
  if (a.menu_id && b.menu_id) return a.menu_id === b.menu_id;
  const na = a.nombre_menu || a.nombre;
  const nb = b.nombre_menu || b.nombre;
  return !!na && na === nb;
};

const ivaExplicito = (l) => {
  for (const campo of ['tipo_iva', 'iva_percent', 'iva']) {
    const v = l?.[campo];
    if (v !== undefined && v !== null && v !== '' && Number.isFinite(Number(v))) return Number(v);
  }
  return null;
};

// Venta sin IVA de una línea: precio_unitario x personas > precio_ud x uds
const ventaLinea = (l) => {
  if (l?.precio_unitario !== undefined && l?.precio_unitario !== null) {
    return (Number(l.precio_unitario) || 0) * (Number(l.num_personas) || 0);
  }
  if (l?.precio_ud && l?.uds) return (Number(l.precio_ud) || 0) * (Number(l.uds) || 0);
  return (Number(l?.precio_por_persona) || 0) * (Number(l?.num_personas) || 0);
};

// Clave estable de cada línea (línea, menú o nombre; si se repite, con su número de repetición)
const clavesLineas = (lineas) => {
  const vistas = {};
  return lineas.map((l) => {
    const base = String(l.linea_id || l.menu_id || l.nombre_menu || l.nombre || 'linea');
    vistas[base] = (vistas[base] || 0) + 1;
    return vistas[base] > 1 ? `${base}-${vistas[base]}` : base;
  });
};


export const lineasSoloEnCabecera = (presupuesto) => {
  const jornadas = Array.isArray(presupuesto?.jornadas) ? presupuesto.jornadas : [];
  if (!presupuesto?.es_multi_evento || jornadas.length === 0) return [];
  const enJornadas = jornadas.flatMap(j => j?.menus_personalizados || []).filter(Boolean);
  if (enJornadas.length === 0) return [];
  const raiz = presupuesto.menus_personalizados || [];
  // Línea de la cabecera de cada línea de jornada (mismo criterio que el servidor)
  const usadas = new Set(enJornadas.map(x => lineaCabecera(x, raiz)).filter(Boolean));
  // Las líneas de venta del alquiler y de las bebidas con importe no son
  // huérfanas: las de evento se cobran una vez desde la cabecera y las de una
  // jornada viven en esa jornada (mismo criterio que el servidor)
  return raiz
    .map((m, indice) => ({ m, indice }))
    .filter(({ m }) => m && !esLineaVentaAparte(m) && !usadas.has(m));
};

// Suplementos del presupuesto que son de una jornada (`jornada_id`: mínimo 40
// personas de sus servicios...), sin las cortesías: van en el subtotal de la
// jornada (mismo criterio que el servidor, lineas_suplementos_raiz; IVA del
// suplemento, 10 % por defecto). { base, cuota, n }
export const suplementosDeJornada = (presupuesto, jornadaId) => {
  const ids = new Set((presupuesto?.jornadas || []).filter(Boolean).map(j => j.id));
  return (presupuesto?.suplementos || [])
    .filter(s => s && !s.es_cortesia && s.jornada_id && s.jornada_id === jornadaId && ids.has(s.jornada_id))
    .reduce((acc, s) => {
      const base = Math.round((Number(s.total) || 0) * 100) / 100;
      if (Math.abs(base) < 0.005) return acc;
      const iva = s.iva === undefined || s.iva === null || s.iva === '' ? 10 : (Number(s.iva) || 0);
      return { base: acc.base + base, cuota: acc.cuota + base * (iva / 100), n: acc.n + 1 };
    }, { base: 0, cuota: 0, n: 0 });
};

const esBarraVinculada = (l) => !!(l && l.es_barra_libre_lunch && l.barra_libre_de_linea_id);

const claseChipApagado = 'border-gray-300 text-gray-600 bg-white hover:border-duetto-primary hover:text-duetto-primary';
const claseChipVerde = 'bg-[#E3F3EC] text-[#0F5E43] border-[#BFE3D2]';
const claseBotonBarra = 'h-11 md:h-9 px-3 rounded-lg border text-sm font-semibold whitespace-nowrap disabled:opacity-50';

const RETARDO_UDS_MS = 600;
const detalleError = (error) => {
  const d = error?.response?.data?.detail;
  if (typeof d === 'string' && d) return d;
  if (d && typeof d === 'object') return d.mensaje || JSON.stringify(d);
  return error?.message || 'Inténtalo de nuevo.';
};

const LineasPorJornada = ({
  presupuesto, onActualizado, barraLibre = null, soloLectura = false, onOrdenCocina = null, onOrdenCocinaNueva = null,
}) => {
  // Móvil (< 768 px): cada línea es una tarjeta en vez de una fila de tabla
  const esMovil = useEsMovil();
  const [ocupado, setOcupado] = useState(null);
  // Ventana abierta: { tipo: 'editar' | 'menus' | 'duplicar' | 'nueva' | 'generar', jornadaId }
  const [modal, setModal] = useState(null);
  // Unidades tecleadas en línea, pendientes de guardar: { [jornadaId]: { [posición]: valor } }
  const [udsEnEdicion, setUdsEnEdicion] = useState({});
  const pendientesRef = useRef({});
  const temporizadoresRef = useRef({});
  const presupuestoRef = useRef(presupuesto);
  presupuestoRef.current = presupuesto;
  const enviarUdsRef = useRef(null);

  const jornadas = useMemo(
    () => (Array.isArray(presupuesto?.jornadas) ? presupuesto.jornadas : []),
    [presupuesto]
  );
  const raiz = useMemo(() => presupuesto?.menus_personalizados || [], [presupuesto]);
  const huerfanas = lineasSoloEnCabecera(presupuesto);
  const presupuestoId = presupuesto?.id;
  const esMultievento = !!presupuesto?.es_multi_evento;

  // Líneas de jornada con precio o IVA distinto a la cabecera (lectura; la
  // misma consulta que el gestor heredado) para "Igualar jornadas a la cabecera"
  const [divergencias, setDivergencias] = useState(null);
  const firmaLineas = useMemo(
    () => JSON.stringify([raiz, jornadas.map(j => j?.menus_personalizados || [])]),
    [raiz, jornadas]
  );
  useEffect(() => {
    if (!presupuestoId || !esMultievento || soloLectura) {
      setDivergencias(null);
      return undefined;
    }
    let vigente = true;
    presupuestosAPI.divergenciasCabeceraMultievento(presupuestoId)
      .then(({ data }) => { if (vigente) setDivergencias(data || null); })
      .catch(() => { if (vigente) setDivergencias(null); });
    return () => { vigente = false; };
  }, [presupuestoId, esMultievento, soloLectura, firmaLineas]);

  const recargar = async (data = null) => {
    if (onActualizado) await onActualizado(data);
  };

  // PUT de una jornada con solo los campos que cambian. true si se guardó
  const guardarJornada = async (jornadaId, cuerpo, { silencioso = false } = {}) => {
    try {
      const { data } = await presupuestosAPI.actualizarJornada(presupuestoId, jornadaId, cuerpo);
      if (!silencioso && data?.aviso_precio) {
        toast({ title: 'Precio de la cabecera', description: data.aviso_precio });
      }
      // Cambio de hora de inicio: los servicios sin hora fija se mueven lo mismo
      if (!silencioso && (data?.servicios_desplazados > 0 || data?.aviso_horas)) {
        const n = data.servicios_desplazados || 0;
        toast({
          title: n > 0 ? `${n === 1 ? '1 servicio movido' : `${n} servicios movidos`} a la nueva hora` : 'Revisa las horas de los servicios',
          description: data.aviso_horas || undefined,
        });
      }
      // La jornada sale de Madrid Central: se ha quitado su suplemento automático
      if (!silencioso && data?.aviso_madrid_central) {
        toast({ title: 'Madrid Central', description: data.aviso_madrid_central });
      }
      await recargar(null);
      return true;
    } catch (error) {
      toast({ variant: 'destructive', title: 'No se ha guardado la jornada', description: detalleError(error) });
      return false;
    }
  };

  // Unidades en línea: se juntan los cambios de la jornada y se guardan con
  // su `menus_personalizados` completo (el mismo camino que "Editar menús")
  const enviarUds = async (jornadaId) => {
    clearTimeout(temporizadoresRef.current[jornadaId]);
    delete temporizadoresRef.current[jornadaId];
    const cambios = pendientesRef.current[jornadaId];
    delete pendientesRef.current[jornadaId];
    if (!cambios || Object.keys(cambios).length === 0) return;
    const jornada = (presupuestoRef.current?.jornadas || []).find(x => x && x.id === jornadaId);
    if (!jornada) return;
    const actuales = (jornada.menus_personalizados || []).filter(Boolean);
    const nuevas = lineasConUdsCambiadas(actuales, cambios);
    if (nuevas.every((l, i) => l === actuales[i])) {
      // Nada que guardar: fuera lo que ya coincide con lo guardado; lo que no
      // es un número válido se queda en el campo (en rojo) hasta corregirlo
      setUdsEnEdicion((prev) => {
        const n = { ...prev };
        const quedan = { ...(n[jornadaId] || {}) };
        Object.keys(cambios).forEach((pos) => {
          if ((parseInt(quedan[pos], 10) || 0) > 0) delete quedan[pos];
        });
        if (Object.keys(quedan).length > 0) n[jornadaId] = quedan;
        else delete n[jornadaId];
        return n;
      });
      return;
    }
    await guardarJornada(jornadaId, { menus_personalizados: nuevas });
    setUdsEnEdicion((prev) => {
      const n = { ...prev };
      const quedan = { ...(n[jornadaId] || {}) };
      Object.keys(cambios).forEach((pos) => { if (quedan[pos] === cambios[pos]) delete quedan[pos]; });
      if (Object.keys(quedan).length > 0) n[jornadaId] = quedan;
      else delete n[jornadaId];
      return n;
    });
  };
  enviarUdsRef.current = enviarUds;

  const teclearUds = (jornadaId, pos, valor) => {
    setUdsEnEdicion(prev => ({ ...prev, [jornadaId]: { ...(prev[jornadaId] || {}), [pos]: valor } }));
    pendientesRef.current[jornadaId] = { ...(pendientesRef.current[jornadaId] || {}), [pos]: valor };
    clearTimeout(temporizadoresRef.current[jornadaId]);
    temporizadoresRef.current[jornadaId] = setTimeout(() => {
      if (enviarUdsRef.current) enviarUdsRef.current(jornadaId);
    }, RETARDO_UDS_MS);
  };

  // Al salir de la pestaña con unidades sin guardar: se guardan ya
  useEffect(() => {
    const temporizadores = temporizadoresRef.current;
    const enviar = enviarUdsRef;
    return () => {
      Object.keys(temporizadores).forEach((jid) => {
        clearTimeout(temporizadores[jid]);
        if (enviar.current) enviar.current(jid);
      });
    };
  }, []);

  const llevarATodas = async () => {
    setOcupado('llevar');
    try {
      const { data } = await presupuestosAPI.llevarCabeceraMultievento(presupuestoId);
      toast({ title: 'Líneas llevadas a las jornadas', description: `${data?.llevadas || 0} ${data?.llevadas === 1 ? 'línea' : 'líneas'} en todas las jornadas, con las personas de cada una.` });
      await recargar(data?.presupuesto || null);
    } catch (error) {
      toast({ variant: 'destructive', title: 'No se han llevado las líneas', description: detalleError(error) });
    } finally {
      setOcupado(null);
    }
  };

  const quitar = async (jornada, linea) => {
    const nombre = linea.nombre_menu || linea.nombre || 'la línea';
    if (!window.confirm(`¿Quitar "${nombre}" de esta jornada?`)) return;
    setOcupado(`${jornada.id}:${nombre}`);
    try {
      const { data } = await presupuestosAPI.quitarLineaMultievento(presupuestoId, {
        menu_id: linea.menu_id || null,
        nombre_menu: linea.menu_id ? null : nombre,
        linea_id: linea.linea_id || null,
        jornadas_ids: [jornada.id],
      });
      await recargar(data?.presupuesto || null);
    } catch (error) {
      toast({ variant: 'destructive', title: 'No se ha quitado la línea', description: detalleError(error) });
    } finally {
      setOcupado(null);
    }
  };

  const crearJornada = async (datos) => {
    try {
      await presupuestosAPI.crearJornada(presupuestoId, { ...datos, menus_personalizados: [] });
      toast({ title: 'Jornada creada', description: 'Añade sus líneas con Editar menús: una jornada sin líneas no cobra ni cocina nada.' });
      await recargar(null);
      return true;
    } catch (error) {
      toast({ variant: 'destructive', title: 'No se ha creado la jornada', description: detalleError(error) });
      return false;
    }
  };

  const generarJornadas = async (datos) => {
    try {
      const { data } = await presupuestosAPI.generarJornadas(presupuestoId, datos);
      toast({ title: 'Jornadas generadas', description: data?.message || undefined });
      // Las jornadas nuevas no copian el alquiler: el servidor lo avisa
      if (data?.aviso) toast({ title: 'Alquiler', description: data.aviso });
      if (data?.aviso_lineas_distintas) {
        toast({ title: 'Revisa las líneas de las jornadas nuevas', description: data.aviso_lineas_distintas });
      }
      await recargar(null);
      return true;
    } catch (error) {
      toast({ variant: 'destructive', title: 'No se han generado las jornadas', description: detalleError(error) });
      return false;
    }
  };

  const duplicarJornada = async (jornadaId, fecha) => {
    try {
      const { data } = await presupuestosAPI.duplicarJornada(presupuestoId, jornadaId, fecha);
      toast({ title: 'Jornada duplicada', description: data?.message || undefined });
      // La jornada nueva no copia el alquiler de la original: el servidor lo avisa
      if (data?.aviso) toast({ title: 'Alquiler', description: data.aviso });
      if (data?.aviso_lineas_distintas) {
        toast({ title: 'Revisa las líneas de la jornada nueva', description: data.aviso_lineas_distintas });
      }
      await recargar(null);
      return true;
    } catch (error) {
      toast({ variant: 'destructive', title: 'No se ha duplicado la jornada', description: detalleError(error) });
      return false;
    }
  };

  const eliminarJornada = async (jornada, numero) => {
    if (!window.confirm(`¿Eliminar la ${textoJornada(jornada, numero).replace(/^J/, 'j')}? Se quitan sus líneas, sus horas y su lugar.`)) return;
    setOcupado(`eliminar:${jornada.id}`);
    try {
      await presupuestosAPI.eliminarJornada(presupuestoId, jornada.id);
      toast({ title: 'Jornada eliminada' });
      await recargar(null);
    } catch (error) {
      // 400 o 409 (facturada...): el motivo del servidor tal cual
      toast({ variant: 'destructive', title: 'No se ha eliminado la jornada', description: detalleError(error) });
    } finally {
      setOcupado(null);
    }
  };

  const igualarACabecera = async () => {
    setOcupado('igualar');
    try {
      const { data } = await presupuestosAPI.igualarCabeceraMultievento(presupuestoId);
      const n = data?.cambiadas || 0;
      toast({ title: 'Jornadas igualadas a la cabecera', description: n === 1 ? '1 línea actualizada.' : `${n} líneas actualizadas.` });
      setDivergencias({ lineas: 0, detalle: [], aviso: null });
      await recargar(null);
    } catch (error) {
      toast({ variant: 'destructive', title: 'No se han igualado las jornadas', description: detalleError(error) });
    } finally {
      setOcupado(null);
    }
  };

  // Chip "Barra libre" del lunch de una jornada (estado del GET barra-libre-lunch).
  // Evento ya celebrado (`aplica: false`): sin chip y la línea vinculada como una normal
  const barraAplica = !!barraLibre?.estado && barraLibre.estado.aplica !== false;
  const entradaBarra = (jornadaId, pos) => (barraAplica ? (barraLibre?.estado?.lineas || []).find(
    (e) => e.jornada_id === jornadaId && e.indice === pos
  ) : null) || null;
  const renderChip = (jornadaId, pos, bloqueado) => {
    const entrada = entradaBarra(jornadaId, pos);
    if (!entrada || !barraLibre?.alternar) return null;
    const clave = `${jornadaId}:${pos}`;
    const guardando = barraLibre.guardando === clave;
    const error = barraLibre.error && barraLibre.error.clave === clave ? barraLibre.error.texto : null;
    let texto = 'Barra libre';
    let titulo = 'Añadir la barra libre del lunch (cubre la bebida incluida)';
    let clases = claseChipApagado;
    if (entrada.legado) {
      texto = 'Barra libre (línea suelta)';
      titulo = 'La barra libre de este lunch es una línea suelta: se gestiona desde su propia línea';
      clases = `${claseChipVerde} cursor-default`;
    } else if (entrada.activa) {
      texto = 'Barra libre activa';
      titulo = 'Quitar la barra libre del lunch';
      clases = claseChipVerde;
    }
    if (bloqueado && !entrada.activa) return null;
    return (
      <>
        <button
          type="button"
          onClick={() => {
            if (bloqueado || entrada.legado || guardando) return;
            barraLibre.alternar(entrada, !entrada.activa, clave);
          }}
          disabled={guardando || bloqueado}
          title={titulo}
          data-testid={`chip-barra-libre-${jornadaId}-${pos}`}
          className={`ml-2 px-2 py-0.5 text-xs rounded-full border transition-colors disabled:opacity-60 ${clases}`}
        >
          {guardando ? 'Guardando...' : texto}
        </button>
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </>
    );
  };
  // "Quitar" de la línea vinculada: apaga el chip de su lunch en esta jornada
  const quitarBarraVinculada = (jornada, linea, pos) => {
    if (!barraLibre?.alternar) return quitar(jornada, linea);
    const entrada = (barraLibre.estado?.lineas || []).find(
      (e) => e.jornada_id === jornada.id && e.linea_id && e.linea_id === linea.barra_libre_de_linea_id
    ) || { linea_id: linea.barra_libre_de_linea_id, jornada_id: jornada.id, indice: null };
    return barraLibre.alternar(entrada, false, `${jornada.id}:barra:${pos}`);
  };

  const ivaDe = (l) => {
    const v = ivaExplicito(l);
    if (v !== null) return v < 1 ? v * 100 : v;
    const par = raiz.find(r => mismaLinea(l, r));
    const vr = par ? ivaExplicito(par) : null;
    if (vr !== null) return vr < 1 ? vr * 100 : vr;
    return 10;
  };

  if (!esMultievento) return null;

  const ordenadas = jornadasOrdenadas(presupuesto);
  const vacias = idsJornadasVacias(presupuesto);
  const numeroDe = (jornadaId) => ordenadas.findIndex(x => x.id === jornadaId) + 1;
  const jornadaModal = modal?.jornadaId ? ordenadas.find(x => x.id === modal.jornadaId) : null;
  const totalServidor = Number(presupuesto?.importe_total);
  const hayTotal = Number.isFinite(totalServidor) && totalServidor > 0;
  const nDivergencias = Number(divergencias?.lineas) || 0;

  return (
    <section className="bg-white border border-[#E1E6E3] rounded-xl overflow-hidden" data-testid="lineas-por-jornada">
      <div className="px-4 sm:px-5 py-4 border-b border-[#E1E6E3] flex flex-col gap-3">
        <div>
          <h2 className="text-xs font-bold tracking-wider uppercase text-[#5B6B64]">Lo que se cobra y se cocina por jornada</h2>
          <p className="text-sm text-[#33443C] mt-1">
            En un multievento cuenta lo de cada jornada. Los suplementos de una jornada (como el mínimo de personas) van en su subtotal; los del evento (transporte, Madrid Central...) van aparte.
            {' '}{TEXTO_PRECIO_EN_CABECERA}: cada jornada usa el precio y el IVA de su línea de la cabecera.
          </p>
        </div>

        {!soloLectura && (
          <div className="flex flex-wrap items-center gap-2" data-testid="barra-jornadas">
            <button
              type="button"
              onClick={() => setModal({ tipo: 'nueva' })}
              disabled={!!ocupado}
              className={`${claseBotonBarra} bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90`}
              data-testid="anadir-jornada"
            >
              Añadir jornada
            </button>
            {ordenadas.length <= 1 && (
              <button
                type="button"
                onClick={() => setModal({ tipo: 'generar' })}
                disabled={!!ocupado}
                className={`${claseBotonBarra} bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50`}
                data-testid="generar-jornadas"
              >
                Generar jornadas
              </button>
            )}
            {ordenadas.length > 0 && (
              <button
                type="button"
                onClick={igualarACabecera}
                disabled={!!ocupado}
                className={`${claseBotonBarra} bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50 inline-flex items-center gap-2`}
                title="Pone en las líneas de las jornadas el precio y el IVA de su línea de la cabecera"
                data-testid="igualar-jornadas-cabecera"
              >
                {ocupado === 'igualar' ? 'Igualando...' : 'Igualar jornadas a la cabecera'}
                {divergencias && (
                  <span
                    className={`rounded-full border px-2 py-px text-xs md:text-[11px] font-bold ${nDivergencias > 0
                      ? 'bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]' : 'bg-[#E3F3EC] border-[#BFE3D2] text-[#0F5E43]'}`}
                    data-testid="insignia-divergencias"
                  >
                    {nDivergencias > 0 ? `${nDivergencias} ${nDivergencias === 1 ? 'distinta' : 'distintas'}` : 'Todo igual'}
                  </span>
                )}
              </button>
            )}
          </div>
        )}
        {!soloLectura && nDivergencias > 0 && divergencias?.aviso && (
          <p className="text-xs rounded-lg border px-2.5 py-1.5 bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]" data-testid="aviso-divergencias-cabecera">
            {divergencias.aviso}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#F7F9F8] px-3 py-2 text-sm text-[#33443C]" data-testid="totales-jornadas">
          <span>
            <strong>{ordenadas.length}</strong> {ordenadas.length === 1 ? 'jornada' : 'jornadas'} · {textoPersonasGestorJornadas(ordenadas)}
          </span>
          {hayTotal && (
            <span>Total del evento: <strong>{formatEuros(totalServidor)}</strong> con IVA</span>
          )}
        </div>
      </div>

      {huerfanas.length > 0 && (
        <div className="mx-4 sm:mx-5 mt-4 rounded-[10px] border px-3.5 py-3 text-sm bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]" data-testid="aviso-lineas-cabecera">
          <p className="font-semibold">Estas líneas están en la cabecera y no en ninguna jornada: no se cobran ni se cocinan.</p>
          <p className="mt-1">{huerfanas.map(h => h.m.nombre_menu || h.m.nombre || 'Línea').join(', ')}</p>
          {!soloLectura && (
            <button
              type="button"
              onClick={llevarATodas}
              disabled={!!ocupado}
              className="mt-2 h-11 md:h-8 px-3 rounded-lg bg-duetto-primary text-white text-sm md:text-xs font-semibold hover:bg-opacity-90 disabled:opacity-50"
              data-testid="llevar-cabecera-a-jornadas"
            >
              {ocupado === 'llevar' ? 'Llevando...' : 'Llevar a todas las jornadas'}
            </button>
          )}
        </div>
      )}

      {ordenadas.length === 0 && (
        <p className="px-4 sm:px-5 py-6 text-sm text-[#5B6B64]">
          No hay jornadas. Añade la primera o genera una por día de un rango de fechas.
        </p>
      )}

      <div className="divide-y divide-[#EEF1EF]">
        {ordenadas.map((j, i) => {
          // Numeradas por orden de fecha (M-97). Cada línea con el precio, el
          // IVA y el concepto de la cabecera
          const numero = i + 1;
          const facturada = !!(j.numero_factura || j.facturada);
          const bloqueada = soloLectura || facturada;
          const lineas = (j.menus_personalizados || []).filter(Boolean).map(l => lineaResuelta(l, raiz));
          const claves = clavesLineas(lineas);
          const sup = suplementosDeJornada(presupuesto, j.id);
          const base = lineas.reduce((s, l) => s + ventaLinea(l), 0) + sup.base;
          const cuota = lineas.reduce((s, l) => s + ventaLinea(l) * (ivaDe(l) / 100), 0) + sup.cuota;
          const enEdicion = udsEnEdicion[j.id] || {};
          return (
            <div key={j.id} className="px-4 sm:px-5 py-4 flex flex-col gap-3" data-testid={`jornada-lineas-${j.id}`}>
              <JornadaCabecera
                presupuesto={presupuesto}
                jornada={j}
                numero={numero}
                vacia={vacias.has(j.id) || lineas.length === 0}
                facturada={facturada}
                soloLectura={soloLectura}
                ocupado={ocupado === `eliminar:${j.id}`}
                onActualizado={onActualizado}
                onEditar={() => setModal({ tipo: 'editar', jornadaId: j.id })}
                onEditarMenus={() => setModal({ tipo: 'menus', jornadaId: j.id })}
                onDuplicar={() => setModal({ tipo: 'duplicar', jornadaId: j.id })}
                onEliminar={() => eliminarJornada(j, numero)}
                onOrdenCocina={onOrdenCocina ? () => onOrdenCocina(j.id) : null}
                onOrdenCocinaNueva={onOrdenCocinaNueva ? () => onOrdenCocinaNueva(j.id) : null}
              />
              <ObservacionesJornada
                jornada={j}
                soloLectura={bloqueada}
                onGuardar={(cuerpo) => guardarJornada(j.id, cuerpo)}
              />
              {lineas.length === 0 ? (
                <p className="text-sm text-[#5B6B64]">
                  Sin líneas: esta jornada no cobra ni cocina nada.
                  {!bloqueada && (
                    <>
                      {' '}
                      <button type="button" onClick={() => setModal({ tipo: 'menus', jornadaId: j.id })}
                        className="font-semibold text-duetto-primary hover:underline" data-testid={`asignar-menus-${j.id}`}>
                        Añadir líneas
                      </button>
                    </>
                  )}
                </p>
              ) : esMovil ? (
                // Móvil: una tarjeta por línea (concepto + importe; uds x precio · IVA;
                // chips; acciones a ancho completo). Mismo estado y handlers que la tabla
                <ul className="flex flex-col gap-2" data-testid={`lineas-jornada-movil-${j.id}`}>
                  {lineas.map((l, pos) => {
                    const nombre = l.nombre_menu || l.nombre || 'Línea';
                    const clave = claves[pos];
                    const distinta = (Number(l.num_personas) || 0) !== (Number(j.num_personas) || 0);
                    const vinculada = barraAplica && esBarraVinculada(l);
                    const aparte = esLineaVentaAparte(l);
                    const udsEditables = !bloqueada && !vinculada && !aparte
                      && l.num_personas !== undefined && l.num_personas !== null;
                    const valorUds = Object.prototype.hasOwnProperty.call(enEdicion, pos)
                      ? enEdicion[pos] : (l.num_personas ?? '');
                    const errorBarra = barraLibre?.error && barraLibre.error.clave === `${j.id}:barra:${pos}`
                      ? barraLibre.error.texto : null;
                    const bebida = esLineaBebida(l);
                    const chip = renderChip(j.id, pos, bloqueada);
                    return (
                      <li key={clave}
                        className={`rounded-lg border border-[#E1E6E3] bg-white p-3 flex flex-col gap-1.5${vinculada ? ' ml-4' : ''}`}
                        data-testid={`tarjeta-linea-jornada-${j.id}-${pos}`}>
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-[15px] leading-snug text-[#16221D] break-words min-w-0">{nombre}</span>
                          <strong className="text-[15px] tabular-nums text-[#16221D] whitespace-nowrap">{formatEuros(ventaLinea(l))}</strong>
                        </div>
                        <p className="text-[13px] text-[#5B6B64] tabular-nums">
                          {(udsEditables ? valorUds : (l.num_personas ?? l.uds)) ?? '-'} uds
                          {' × '}{formatEuros(l.precio_unitario ?? l.precio_ud)} · IVA {ivaDe(l)} %
                        </p>
                        {vinculada && <p className="text-xs text-[#5B6B64]">Sigue a las personas del lunch</p>}
                        {!vinculada && distinta && (
                          <p className="text-xs font-semibold text-[#7A3A06]">Número distinto al de la jornada</p>
                        )}
                        {(bebida || chip || vinculada) && (
                          <div className="flex flex-wrap items-center gap-1.5 [&>button]:ml-0 [&>button]:min-h-[40px] [&>button]:px-3 [&>button]:text-[13px] [&>p]:w-full [&>p]:mt-0">
                            {bebida && (
                              <span className="px-2 py-0.5 text-xs rounded-full border bg-sky-50 text-sky-800 border-sky-200"
                                data-testid={`linea-bebida-jornada-${j.id}-${pos}`}>
                                Bebidas
                              </span>
                            )}
                            {chip}
                            {vinculada && (
                              <span className={`px-2 py-0.5 text-xs rounded-full border ${claseChipVerde}`}
                                data-testid={`barra-libre-vinculada-${j.id}-${pos}`}>
                                Cubre la bebida del lunch
                              </span>
                            )}
                          </div>
                        )}
                        {errorBarra && <p className="text-xs text-red-600">{errorBarra}</p>}
                        {udsEditables && (
                          <label className="flex items-center justify-between gap-3 text-[13px] text-[#33443C]">
                            Unidades
                            <input
                              type="number"
                              min="1"
                              inputMode="numeric"
                              value={valorUds}
                              onChange={(e) => teclearUds(j.id, pos, e.target.value)}
                              className={`w-28 h-11 px-3 text-base text-right tabular-nums rounded-lg border focus:outline-none focus:ring-2 focus:ring-duetto-primary ${
                                (parseInt(valorUds, 10) || 0) > 0 ? 'border-[#CBD3CF]' : 'border-red-400'}`}
                              aria-label={`Unidades de ${nombre}`}
                              data-testid={`uds-linea-jornada-${j.id}-${pos}`}
                            />
                          </label>
                        )}
                        {bebida ? (
                          <p className="text-xs text-[#5B6B64]">El importe de las bebidas se cambia en la pestaña Bebidas.</p>
                        ) : !bloqueada && (
                          <button
                            type="button"
                            onClick={() => (vinculada ? quitarBarraVinculada(j, l, pos) : quitar(j, l))}
                            disabled={!!ocupado || (vinculada && barraLibre?.guardando === `${j.id}:barra:${pos}`)}
                            className="mt-1 w-full h-11 rounded-lg border border-red-200 bg-white text-sm font-semibold text-red-700 active:bg-red-50 disabled:opacity-50"
                            data-testid={`quitar-linea-jornada-${j.id}`}
                          >
                            Quitar de la jornada
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="text-left text-xs font-bold uppercase tracking-wider text-[#5B6B64]">
                        <th className="py-1.5 pr-2">Línea</th>
                        <th className="py-1.5 px-2 text-right">Uds</th>
                        <th className="py-1.5 px-2 text-right">Precio</th>
                        <th className="py-1.5 px-2 text-right">IVA</th>
                        <th className="py-1.5 px-2 text-right">Importe</th>
                        <th className="py-1.5 pl-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {lineas.map((l, pos) => {
                        const nombre = l.nombre_menu || l.nombre || 'Línea';
                        const clave = claves[pos];
                        const distinta = (Number(l.num_personas) || 0) !== (Number(j.num_personas) || 0);
                        const vinculada = barraAplica && esBarraVinculada(l);
                        const aparte = esLineaVentaAparte(l);
                        const udsEditables = !bloqueada && !vinculada && !aparte
                          && l.num_personas !== undefined && l.num_personas !== null;
                        const valorUds = Object.prototype.hasOwnProperty.call(enEdicion, pos)
                          ? enEdicion[pos] : (l.num_personas ?? '');
                        const errorBarra = barraLibre?.error && barraLibre.error.clave === `${j.id}:barra:${pos}`
                          ? barraLibre.error.texto : null;
                        return (
                          <tr key={clave} className="border-t border-[#EEF1EF]">
                            <td className={`py-1.5 pr-2 text-[#16221D]${vinculada ? ' pl-6' : ''}`}>
                              {nombre}
                              {esLineaBebida(l) && (
                                <span className="ml-2 px-2 py-0.5 text-xs rounded-full border bg-sky-50 text-sky-800 border-sky-200"
                                  data-testid={`linea-bebida-jornada-${j.id}-${pos}`}>
                                  Bebidas
                                </span>
                              )}
                              {renderChip(j.id, pos, bloqueada)}
                              {vinculada && (
                                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full border ${claseChipVerde}`}
                                  data-testid={`barra-libre-vinculada-${j.id}-${pos}`}>
                                  Cubre la bebida del lunch
                                </span>
                              )}
                              {errorBarra && <p className="mt-1 text-xs text-red-600">{errorBarra}</p>}
                            </td>
                            <td className={`py-1.5 px-2 text-right tabular-nums ${distinta && !vinculada ? 'text-[#7A3A06] font-semibold' : ''}${vinculada ? ' text-gray-500' : ''}`}
                              title={vinculada ? 'Sigue a las personas del lunch' : (distinta ? 'Número distinto al de la jornada' : undefined)}>
                              {udsEditables ? (
                                <input
                                  type="number"
                                  min="1"
                                  inputMode="numeric"
                                  value={valorUds}
                                  onChange={(e) => teclearUds(j.id, pos, e.target.value)}
                                  className={`w-20 h-8 px-2 text-right tabular-nums rounded-lg border focus:outline-none focus:ring-2 focus:ring-duetto-primary ${
                                    (parseInt(valorUds, 10) || 0) > 0 ? 'border-[#CBD3CF]' : 'border-red-400'}`}
                                  aria-label={`Unidades de ${nombre}`}
                                  data-testid={`uds-linea-jornada-${j.id}-${pos}`}
                                />
                              ) : (l.num_personas ?? l.uds ?? '-')}
                            </td>
                            <td className="py-1.5 px-2 text-right tabular-nums" title={TEXTO_PRECIO_EN_CABECERA}>{formatEuros(l.precio_unitario ?? l.precio_ud)}</td>
                            <td className="py-1.5 px-2 text-right tabular-nums">{ivaDe(l)} %</td>
                            <td className="py-1.5 px-2 text-right tabular-nums font-semibold">{formatEuros(ventaLinea(l))}</td>
                            <td className="py-1.5 pl-2 text-right">
                              {esLineaBebida(l) ? (
                                <span className="text-xs text-[#5B6B64]" title="El importe de las bebidas se cambia en la pestaña Bebidas">
                                  En Bebidas
                                </span>
                              ) : !bloqueada && (
                              <button
                                type="button"
                                onClick={() => (vinculada ? quitarBarraVinculada(j, l, pos) : quitar(j, l))}
                                disabled={!!ocupado || (vinculada && barraLibre?.guardando === `${j.id}:barra:${pos}`)}
                                className="text-xs font-semibold text-red-700 hover:underline disabled:opacity-50"
                                data-testid={`quitar-linea-jornada-${j.id}`}
                              >
                                Quitar
                              </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-sm text-right text-[#33443C]">
                Subtotal jornada {numero}{sup.n > 0 ? ` con ${sup.n === 1 ? 'su suplemento' : 'sus suplementos'}` : ''}: <strong>{formatEuros(base)}</strong> sin IVA · {formatEuros(base + cuota)} con IVA
              </p>
            </div>
          );
        })}
      </div>
      {hayTotal && (
        <div className="px-4 sm:px-5 py-3 bg-[#F7F9F8] border-t border-[#E1E6E3] text-sm text-right text-[#16221D]" data-testid="total-multievento">
          Total del presupuesto (jornadas, suplementos y alquiler): <strong>{formatEuros(totalServidor)}</strong> con IVA
        </div>
      )}

      {modal?.tipo === 'nueva' && (
        <NuevaJornadaModal presupuesto={presupuesto} onConfirmar={crearJornada} onCerrar={() => setModal(null)} />
      )}
      {modal?.tipo === 'generar' && (
        <GenerarJornadasModal presupuesto={presupuesto} onConfirmar={generarJornadas} onCerrar={() => setModal(null)} />
      )}
      {modal?.tipo === 'editar' && jornadaModal && (
        <EditarJornadaModal
          presupuesto={presupuesto}
          jornada={jornadaModal}
          numero={numeroDe(jornadaModal.id)}
          onGuardar={(cuerpo) => guardarJornada(jornadaModal.id, cuerpo)}
          onCerrar={() => setModal(null)}
        />
      )}
      {modal?.tipo === 'menus' && jornadaModal && (
        <EditarMenusJornadaModal
          presupuesto={presupuesto}
          jornada={jornadaModal}
          numero={numeroDe(jornadaModal.id)}
          onGuardar={(menus) => guardarJornada(jornadaModal.id, { menus_personalizados: menus })}
          onCerrar={() => setModal(null)}
        />
      )}
      {modal?.tipo === 'duplicar' && jornadaModal && (
        <DuplicarJornadaModal
          jornada={jornadaModal}
          numero={numeroDe(jornadaModal.id)}
          onConfirmar={(fecha) => duplicarJornada(jornadaModal.id, fecha)}
          onCerrar={() => setModal(null)}
        />
      )}
    </section>
  );
};

export default LineasPorJornada;
